import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { SlidersHorizontal, Save, RefreshCw, Loader } from 'lucide-react';

const Parameters = () => {
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  // Fetch all config parameters
  const { data, isLoading, error } = useQuery('adminConfig', async () => {
    const response = await axios.get('/api/config/admin');
    return response.data.config;
  });

  // Reset edited values when data loads
  useEffect(() => {
    if (data) {
      const values = {};
      data.forEach(param => {
        values[param.key] = param.value ?? param.defaultValue;
      });
      setEditedValues(values);
      setHasChanges(false);
    }
  }, [data]);

  // Mutation to update a single config value
  const updateConfig = useMutation(
    async ({ key, value }) => {
      const response = await axios.put(`/api/config/admin/${key}`, { value });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminConfig');
      }
    }
  );

  // Mutation to regenerate QR code - sends the current form value
  const regenerateQR = useMutation(
    async (url) => {
      const response = await axios.post('/api/config/admin/regenerate-qr', { url });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(`QR code regenerated for: ${data.url}`);
        // Clear the changed state for qr_code_url since it's now saved
        setEditedValues(prev => {
          const { qr_code_url, ...rest } = prev;
          return rest;
        });
        queryClient.invalidateQueries('adminConfig');
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to regenerate QR code');
      }
    }
  );

  const handleValueChange = (key, value, type) => {
    let newValue = value;
    if (type === 'boolean') {
      newValue = value ? 'true' : 'false';
    }
    setEditedValues(prev => ({ ...prev, [key]: newValue }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    const originalValues = {};
    data.forEach(param => {
      originalValues[param.key] = param.value ?? param.defaultValue;
    });

    const changedParams = Object.entries(editedValues).filter(
      ([key, value]) => value !== originalValues[key]
    );

    if (changedParams.length === 0) {
      toast.success('No changes to save');
      return;
    }

    try {
      for (const [key, value] of changedParams) {
        await updateConfig.mutateAsync({ key, value });
      }
      toast.success(`Saved ${changedParams.length} parameter(s)`);
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save parameters');
    }
  };

  const renderInput = (param) => {
    const value = editedValues[param.key] ?? param.value ?? param.defaultValue;
    const isChanged = value !== (param.value ?? param.defaultValue);
    const inputStyle = {
      padding: '8px 12px',
      borderRadius: '4px',
      border: isChanged ? '2px solid #90EE90' : '1px solid #ddd',
      backgroundColor: isChanged ? '#f0fff0' : '#fff',
      fontSize: '14px',
      width: '100%',
      maxWidth: '400px'
    };

    switch (param.type) {
      case 'boolean':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={value === 'true' || value === true}
              onChange={(e) => handleValueChange(param.key, e.target.checked, 'boolean')}
              style={{ width: '18px', height: '18px' }}
            />
            <span>{value === 'true' || value === true ? 'Enabled' : 'Disabled'}</span>
          </label>
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(param.key, e.target.value, 'number')}
            style={inputStyle}
          />
        );
      case 'string':
      default:
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="text"
              value={value}
              onChange={(e) => handleValueChange(param.key, e.target.value, 'string')}
              style={inputStyle}
            />
            {param.key === 'qr_code_url' && (
              <button
                onClick={() => regenerateQR.mutate(value)}
                disabled={regenerateQR.isLoading}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {regenerateQR.isLoading ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />}
                Regenerate QR
              </button>
            )}
          </div>
        );
    }
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error Loading Parameters</h2>
        <p>{error.response?.data?.error || 'Failed to load configuration'}</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <SlidersHorizontal size={24} />
          System Parameters
        </h2>
        <button
          onClick={handleSave}
          disabled={!hasChanges || updateConfig.isLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: hasChanges ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: hasChanges ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {updateConfig.isLoading ? <Loader size={16} className="spin" /> : <Save size={16} />}
          Save Changes
        </button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spin" />
          <p>Loading parameters...</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {data?.map(param => (
            <div
              key={param.key}
              style={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '20px'
              }}
            >
              <div style={{ marginBottom: '10px' }}>
                <strong style={{ fontSize: '16px', color: '#333' }}>
                  {param.key.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </strong>
                <span style={{
                  marginLeft: '10px',
                  fontSize: '12px',
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  {param.type}
                </span>
              </div>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                {param.description}
              </p>
              {renderInput(param)}
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                Default: {param.defaultValue}
                {param.updatedAt && (
                  <span> • Last updated: {new Date(param.updatedAt).toLocaleString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Parameters;
